import { useCallback, useState } from 'react';

// A plan refusal from the backend is HTTP 402 with a body shaped like:
//   { detail: "<human sentence>", plan_gate: { feature, current_plan,
//     required_plan, upgrade_url, can_upgrade } }
// These helpers pull that block out of a failed request so the UI can show the
// shared upgrade dialog instead of a raw status code.

export function getPlanGate(body) {
  if (body && typeof body === 'object' && body.plan_gate) {
    return { gate: body.plan_gate, message: body.detail || '' };
  }
  return null;
}

// Inspect a failed fetch Response. Returns { gate, message } or null.
export async function planGateFromResponse(response) {
  try {
    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('application/json')) return null;
    const body = await response.clone().json();
    return getPlanGate(body);
  } catch {
    return null;
  }
}

// Inspect an axios-style error (err.response.data) for a plan_gate block.
export function planGateFromError(error) {
  return getPlanGate(error?.response?.data) || getPlanGate(error?.data) || null;
}

// Pure decision model for the PlanLimitDialog — kept dependency-free so it can
// be machine-tested without a DOM. The upgrade target ALWAYS comes from the
// server (gate.upgrade_url), never hard-coded, and the upgrade action is hidden
// when the server says upgrading cannot help (can_upgrade === false).
export function planDialogModel(gate) {
  return {
    showUpgrade: gate?.can_upgrade !== false,
    upgradeUrl: gate?.upgrade_url || '/dashboard/settings?tab=plans',
    hasServerUrl: !!gate?.upgrade_url,
  };
}

// State manager for the PlanLimitDialog. `show(pg)` returns true when it opened
// (i.e. the failure really was a plan gate), so callers can early-return.
export function usePlanGate() {
  const [state, setState] = useState({ open: false, gate: null, message: '' });
  const show = useCallback((pg) => {
    if (!pg || !pg.gate) return false;
    setState({ open: true, gate: pg.gate, message: pg.message });
    return true;
  }, []);
  const setOpen = useCallback((open) => setState((s) => ({ ...s, open })), []);
  return { ...state, show, setOpen };
}
